import {
  DoubleMaterialityESRSAreaEnum,
  DoubleMaterialityStakeholderType,
} from "@green0meter/api-client";
import { useSnackbar } from "notistack";
import React, { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useESRSLabels } from "../../../Stakeholders/components/useESRSLabels";

import {
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@components/base";
import { Drawer } from "@components/common";
import { useUserStore } from "@store/user";
import { useGenerateQuestions } from "@api";

interface AIAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  topics: DoubleMaterialityESRSAreaEnum[];
  stakeholderType: DoubleMaterialityStakeholderType | "both";
  language: string;
}

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "cz", label: "Czech" },
];

const AIAssistantDrawer: FC<AIAssistantDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const { generateQuestions, isPendingGenerateQuestions } =
    useGenerateQuestions(user);
  const { ESRS_OPTIONS } = useESRSLabels();

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      topics: [],
      stakeholderType: "both",
      language: "en",
    },
  });

  const onSubmit = async (data: FormData): Promise<void> => {
    try {
      let results = [];

      if (data.stakeholderType === "both") {
        results = await Promise.all([
          generateQuestions({
            topics: data.topics,
            stakeholderType: DoubleMaterialityStakeholderType.Internal,
            language: data.language,
          }),
          generateQuestions({
            topics: data.topics,
            stakeholderType: DoubleMaterialityStakeholderType.External,
            language: data.language,
          }),
        ]);
      } else {
        const result = await generateQuestions({
          topics: data.topics,
          stakeholderType: data.stakeholderType,
          language: data.language,
        });

        results = [result];
      }

      const totalGenerated = results.flat().length;

      onClose();

      if (totalGenerated === 0) {
        enqueueSnackbar(
          t(
            "doubleMateriality.questions.noQuestionsGenerated",
            "No questions were generated. Please check if assessments exist for the selected topics.",
          ),
          { variant: "warning" },
        );
      } else {
        enqueueSnackbar(
          t(
            "doubleMateriality.questions.generateSuccess",
            `${totalGenerated} question(s) generated successfully`,
          ),
          { variant: "success" },
        );
      }
    } catch {
      enqueueSnackbar(
        t(
          "doubleMateriality.questions.generateError",
          "Failed to generate questions",
        ),
        { variant: "error" },
      );
    }
  };

  const content = (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Typography variant="h6">
        {t("doubleMateriality.questions.questionGenerator", "Question generator")}
      </Typography>

      <Controller
        name="topics"
        control={control}
        render={({ field }) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t("doubleMateriality.questions.topic", "Topic")}
            </Typography>
            <Select
              {...field}
              multiple
              renderValue={(selected) =>
                (selected as DoubleMaterialityESRSAreaEnum[])
                  .map(
                    (value) =>
                      ESRS_OPTIONS.find((opt) => opt.enumValue === value)?.label ||
                      String(value),
                  )
                  .join(", ")
              }
            >
              {ESRS_OPTIONS.map((option) => (
                <MenuItem key={`topic-${option.code}`} value={option.enumValue}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
      />

      <Controller
        name="stakeholderType"
        control={control}
        render={({ field }) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t("doubleMateriality.questions.stakeholderType", "Stakeholder type")}
            </Typography>
            <RadioGroup {...field}>
              <FormControlLabel
                value={DoubleMaterialityStakeholderType.Internal}
                control={<Radio />}
                label={t("doubleMateriality.stakeholder.internal", "Internal")}
              />
              <FormControlLabel
                value={DoubleMaterialityStakeholderType.External}
                control={<Radio />}
                label={t("doubleMateriality.stakeholder.external", "External")}
              />
              <FormControlLabel
                value="both"
                control={<Radio />}
                label={t("doubleMateriality.stakeholder.both", "Both")}
              />
            </RadioGroup>
          </Stack>
        )}
      />

      <Controller
        name="language"
        control={control}
        render={({ field }) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {t(
                "doubleMateriality.questions.chooseLanguage",
                "Choose language in which you would like to generate the questions",
              )}
            </Typography>
            <Select {...field}>
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
      />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: "auto" }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isPendingGenerateQuestions}
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isPendingGenerateQuestions}
          startIcon={
            isPendingGenerateQuestions ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isPendingGenerateQuestions
            ? t("doubleMateriality.questions.thinking", "Thinking...")
            : t("doubleMateriality.questions.generate", "Generate")}
        </Button>
      </Stack>
    </Stack>
  );

  return <Drawer open={open} onClose={onClose} title="" content={content} />;
};

export default AIAssistantDrawer;
